import { useEffect, useRef } from 'react'
import { usePrimateAuth } from '../../hooks/usePrimateAuth'
import { stripCompiledQueryEnvelope } from '../../core/components/v2/pv2-shared'
// PRI-496 Lane B: session restore reads the public GET /v1/analyses
// (?status=analyzing|queued) instead of internal /v1/video/active-job.
import { getActiveAnalysis } from '../../core/lib/api/public-inference'

/**
 * useSessionRestore — Phase 5 (PRI-291)
 *
 * Extracts the "restore active job on page refresh" effect from PrimateVisionV2Inner.
 * On mount (when signed in + auth loaded + idle + videos loaded), this effect checks
 * if there's an active job in progress and restores the polling state, allowing users
 * to resume watching inference results after a page refresh.
 *
 * The hook receives all needed state setters as parameters since it's called from
 * PrimateVisionV2Inner which is outside the V2Providers context tree.
 */
export function useSessionRestore({
  isSignedIn,
  authLoaded,
  userVideosLoaded,
  pageState,
  pageStateRef,
  setJobId,
  setDisplayedJobId,
  videoKeyRef,
  setActivePrompt,
  setSelectedPrompt,
  setQueuePosition,
  setPageStateSync,
  startPolling,
}: {
  isSignedIn: boolean | null | undefined
  authLoaded: boolean
  userVideosLoaded: boolean
  pageState: string
  pageStateRef: React.MutableRefObject<string>
  setJobId: (id: string) => void
  setDisplayedJobId: (id: string) => void
  videoKeyRef: React.MutableRefObject<string | null>
  setActivePrompt: (prompt: string) => void
  setSelectedPrompt: (prompt: string) => void
  setQueuePosition: (pos: number) => void
  setPageStateSync: (state: import('../../hooks/types').PageState) => void
  startPolling: (jobId: string, prompt: string, delay: number) => void
}) {
  const { getToken } = usePrimateAuth()

  // Stable ref to avoid stale closure in async callback
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  useEffect(() => {
    if (!isSignedIn || !authLoaded || pageState !== 'idle' || !userVideosLoaded) return
    const restoreJob = async () => {
      try {
        const gt = getTokenRef.current
        // PRI-371: use the default Clerk session token (the API validates that);
        // the "supabase" JWT template doesn't exist and only produced a console 404.
        const token = gt ? await gt().catch(() => null) : null
        if (pageStateRef.current !== 'idle') return
        if (!token) return
        // PRI-496 Lane B: public analyses list — auth rides the shared token
        // registry (the Clerk resolver is registered by this point; the
        // explicit-token fetch above stays only as the signed-in gate).
        const data = await getActiveAnalysis()
        if (pageStateRef.current !== 'idle') return
        if (!data) return
        if (!data.job_id || !['queued', 'running'].includes(data.status ?? '')) return
        if (pageStateRef.current !== 'idle') return
        setJobId(data.job_id)
        setDisplayedJobId(data.job_id)
        videoKeyRef.current = data.job_id
        if (data.prompt) {
          // Restored sessions from the API can carry the wire-format prompt
          // ("<rawPrompt>|||COMPILED_QUERY:<base64>|||") if the row was saved
          // pre-fix. Strip the envelope so activePrompt + selectedPrompt are
          // always clean UI text; the wire format isn't needed for display
          // and re-running uses the structured query from a fresh compile.
          const cleanPrompt = stripCompiledQueryEnvelope(data.prompt)
          setActivePrompt(cleanPrompt)
          setSelectedPrompt(cleanPrompt)
        }
        if (data.queue_position) setQueuePosition(data.queue_position)
        setPageStateSync('processing')
        startPolling(data.job_id, data.prompt ?? '', 0)
      } catch { /* non-fatal - idle state is fine if no active job */ }
    }
    void restoreJob()
  }, [isSignedIn, authLoaded, userVideosLoaded]) // eslint-disable-line react-hooks/exhaustive-deps
}
